import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent, type FormEvent } from 'react';
import { useBlocker } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowDown, ArrowUp, BookmarkPlus, ChevronDown, ChevronRight, Columns2, GlassWater, GripVertical, Plus, Rows2, Spline, Trash2, X } from 'lucide-react';
import { blockMinutes, blockStartTimes, diagramIsEmpty, MAX_BREAK, planMinutes, planWarnings, readPlan, sectionBreaks, type Drill, type Plan, type PlanItem } from '@hoopify/shared';
import { api, ApiError, errorMessage } from '../../lib/api';
import { formatMinutes, formatTime } from '../../lib/dates';
import * as P from '../../lib/plan';
import { Alert, Button, Dialog } from '../ui';
import { DiagramView } from '../diagram/DiagramView';
import { DiagramEditorDialog } from '../diagram/DiagramEditor';
import { TagPicker } from '../TagPicker';
import { LibraryPanel } from './LibraryPanel';
import { QuickAdd } from './QuickAdd';

export interface PlanMeta {
  /** Templates have no start time; the gutter then shows minutes from the start. */
  startTime: string | null;
  durationMinutes: number;
  courtsAvailable: number | null;
  playersExpected: number | null;
}

/** Where a drill from the library (or a quick add) goes. */
export type Target = { kind: 'section'; sectionId: string } | { kind: 'alongside'; blockId: string };

/** A drill being dragged in from the library: `make` builds a fresh plan item each time. */
export type DrillSource = { title: string; make: () => PlanItem };
type Drag = { kind: 'block'; blockId: string } | { kind: 'drill'; source: DrillSource } | null;

export function PlanBuilder({
  initialPlan,
  version: initialVersion,
  meta,
  readOnly,
  save,
}: {
  initialPlan: Plan;
  version: number;
  meta: PlanMeta;
  readOnly?: boolean;
  save: (plan: Plan, version: number) => Promise<{ plan: Plan; version: number }>;
}) {
  // Plans saved by older versions may lack newer fields; fill them in.
  const [plan, setPlan] = useState(() => readPlan(initialPlan));
  const [version, setVersion] = useState(initialVersion);
  const [dirty, setDirty] = useState(false);
  const [target, setTarget] = useState<Target>(() => ({ kind: 'section', sectionId: initialPlan.sections[0]?.id ?? '' }));
  const [adding, setAdding] = useState<Target | null>(null);
  const [drag, setDrag] = useState<Drag>(null);
  const [libraryOpen, setLibraryOpen] = useState(false);

  // A fresh copy from the server (after someone else's save, say) replaces ours when we have no edits.
  useEffect(() => {
    if (!dirty) {
      setPlan(readPlan(initialPlan));
      setVersion(initialVersion);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPlan, initialVersion]);

  const edit = useCallback((fn: (p: Plan) => Plan) => {
    setPlan((p) => fn(p));
    setDirty(true);
  }, []);

  const saving = useMutation({
    mutationFn: () => save(P.tidy(plan), version),
    onSuccess: (r) => {
      setPlan(r.plan);
      setVersion(r.version);
      setDirty(false);
    },
  });
  const conflict = saving.error instanceof ApiError && saving.error.status === 409;

  // Ctrl/Cmd+S saves.
  useEffect(() => {
    if (readOnly) return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        if (dirty && !saving.isPending) saving.mutate();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [dirty, readOnly, saving]);

  // Don't lose edits by closing the tab or navigating away.
  useEffect(() => {
    if (!dirty) return;
    const onUnload = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, [dirty]);
  const blocker = useBlocker(({ currentLocation, nextLocation }) => dirty && currentLocation.pathname !== nextLocation.pathname);

  const starts = useMemo(() => blockStartTimes(plan, meta.startTime ?? '00:00'), [plan, meta.startTime]);
  const warnings = useMemo(() => planWarnings(plan, meta), [plan, meta]);
  const total = planMinutes(plan);
  const breaks = useMemo(() => sectionBreaks(plan), [plan]);

  const addDrill = (source: DrillSource, to: Target = target) => {
    const item = source.make();
    edit((p) => (to.kind === 'alongside' ? P.addAlongside(p, to.blockId, item) : P.addBlock(p, to.sectionId, item)));
    if (to.kind === 'alongside') setTarget({ kind: 'section', sectionId: sectionOfBlock(plan, to.blockId) ?? plan.sections[0]?.id ?? '' });
    setAdding(null);
  };

  const timeLabel = (blockId: string) => {
    const t = starts.get(blockId) ?? '00:00';
    if (meta.startTime) return formatTime(t).replace(' ', ' ');
    const [h, m] = t.split(':').map(Number) as [number, number];
    return `${h * 60 + m} min`;
  };

  // ---------- Drag and drop ----------

  const dropAt = (sectionId: string, index: number) => (e: DragEvent) => {
    e.preventDefault();
    if (!drag) return;
    if (drag.kind === 'block') edit((p) => P.moveBlock(p, drag.blockId, sectionId, index));
    else edit((p) => P.addBlock(p, sectionId, drag.source.make(), index));
    setDrag(null);
  };
  const dropAlongside = (blockId: string) => (e: DragEvent) => {
    e.preventDefault();
    if (drag?.kind === 'drill') addDrill(drag.source, { kind: 'alongside', blockId });
    setDrag(null);
  };

  return (
    <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_300px] lg:gap-8">
      <div className="min-w-0">
        {!readOnly && (
          <SaveBar
            total={total}
            duration={meta.durationMinutes}
            dirty={dirty}
            saving={saving.isPending}
            savedOnce={saving.isSuccess}
            onSave={() => saving.mutate()}
            onOpenLibrary={() => setLibraryOpen(true)}
          />
        )}
        {readOnly && <TotalBar total={total} duration={meta.durationMinutes} />}

        {conflict && (
          <div className="mb-4">
            <Alert>
              <p>{errorMessage(saving.error)}</p>
              <button className="mt-1 font-medium underline" onClick={() => window.location.reload()}>
                Load the latest version
              </button>
            </Alert>
          </div>
        )}
        {saving.isError && !conflict && (
          <div className="mb-4">
            <Alert>{errorMessage(saving.error)}</Alert>
          </div>
        )}
        {warnings.filter((w) => !w.blockId).map((w) => (
          <div key={w.message} className="mb-4">
            <Alert tone="warn">{w.message}</Alert>
          </div>
        ))}

        <ol className="space-y-7">
          {plan.sections.map((section, si) => {
            const sectionMinutes = section.blocks.reduce((n, b) => n + blockMinutes(b), 0);
            return (
              <li key={section.id} aria-label={section.title}>
                <SectionHeader
                  title={section.title}
                  minutes={sectionMinutes}
                  readOnly={readOnly}
                  canRemove={!section.blocks.length && plan.sections.length > 1}
                  first={si === 0}
                  last={si === plan.sections.length - 1}
                  onRename={(t) => edit((p) => P.renameSection(p, section.id, t))}
                  onMove={(d) => edit((p) => P.moveSection(p, section.id, d))}
                  onRemove={() => edit((p) => P.removeSection(p, section.id))}
                />
                <div
                  className={`mt-2 rounded-lg ${section.blocks.length ? '' : 'border border-dashed border-line-strong'}`}
                  onDragOver={(e) => drag && !section.blocks.length && e.preventDefault()}
                  onDrop={!section.blocks.length ? dropAt(section.id, 0) : undefined}
                >
                  {!section.blocks.length && (
                    <p className="px-4 py-3 text-[14px] text-faint">{readOnly ? 'Nothing planned here.' : drag ? 'Drop here' : 'No drills yet.'}</p>
                  )}
                  {section.blocks.map((block, bi) => (
                    <div key={block.id}>
                      {drag && !readOnly && <DropLine onDrop={dropAt(section.id, bi)} />}
                      <BlockRow
                        time={timeLabel(block.id)}
                        minutes={blockMinutes(block)}
                        items={block.items}
                        readOnly={readOnly}
                        warnings={warnings.filter((w) => w.blockId === block.id).map((w) => w.message)}
                        dragging={drag?.kind === 'block' && drag.blockId === block.id}
                        acceptsDrill={drag?.kind === 'drill' && block.items.length < 6}
                        onDragStart={() => setDrag({ kind: 'block', blockId: block.id })}
                        onDragEnd={() => setDrag(null)}
                        onDropAlongside={dropAlongside(block.id)}
                        onNudge={(d) => edit((p) => P.nudgeBlock(p, block.id, d))}
                        onAddAlongside={() => {
                          setAdding({ kind: 'alongside', blockId: block.id });
                          setTarget({ kind: 'alongside', blockId: block.id });
                        }}
                        onItem={(itemId, changes) => edit((p) => P.updateItem(p, itemId, changes))}
                        onRemoveItem={(itemId) => edit((p) => P.removeItem(p, itemId))}
                        onSplit={(itemId) => edit((p) => P.splitItem(p, itemId))}
                        onSavedToLibrary={(itemId, drillId) => edit((p) => P.updateItem(p, itemId, { sourceDrillId: drillId }))}
                      />
                      {adding?.kind === 'alongside' && adding.blockId === block.id && (
                        <QuickAdd
                          label="Add a drill at the same time"
                          onAdd={(item) => {
                            edit((p) => P.addAlongside(p, block.id, item));
                            setAdding(null);
                            setTarget({ kind: 'section', sectionId: section.id });
                          }}
                          onCancel={() => {
                            setAdding(null);
                            setTarget({ kind: 'section', sectionId: section.id });
                          }}
                          onLibrary={() => setLibraryOpen(true)}
                        />
                      )}
                    </div>
                  ))}
                  {drag && !readOnly && section.blocks.length > 0 && <DropLine onDrop={dropAt(section.id, section.blocks.length)} />}
                </div>
                {!readOnly &&
                  (adding?.kind === 'section' && adding.sectionId === section.id ? (
                    <QuickAdd
                      label={`Add to ${section.title}`}
                      onAdd={(item) => {
                        edit((p) => P.addBlock(p, section.id, item));
                      }}
                      onCancel={() => setAdding(null)}
                      onLibrary={() => setLibraryOpen(true)}
                    />
                  ) : (
                    <button
                      className="mt-2 inline-flex items-center gap-1.5 rounded-md px-2 py-1.5 text-[14px] font-medium text-action hover:bg-action-soft"
                      onClick={() => {
                        setAdding({ kind: 'section', sectionId: section.id });
                        setTarget({ kind: 'section', sectionId: section.id });
                      }}
                    >
                      <Plus className="size-4" aria-hidden /> Add drill
                    </button>
                  ))}
                {si < plan.sections.length - 1 && section.blocks.length > 0 && (
                  <BreakRow
                    time={breaks[si] ? timeLabel(`break:${section.id}`) : null}
                    value={section.breakAfter}
                    effective={breaks[si]!}
                    readOnly={readOnly}
                    onChange={(m) => edit((p) => P.setBreak(p, section.id, m))}
                  />
                )}
              </li>
            );
          })}
        </ol>

        {!readOnly && (
          <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3">
            <button className="btn-secondary" onClick={() => edit((p) => P.addSection(p, 'New part'))}>
              <Plus className="size-4" aria-hidden /> Add a part
            </button>
            {plan.sections.length > 1 && (
              <div className="flex items-center gap-2 text-[14px]">
                <span className="text-muted">Drink breaks between parts:</span>
                <BreakPicker
                  value={plan.sections.slice(0, -1).every((s) => s.breakAfter === plan.sections[0]!.breakAfter) ? plan.sections[0]!.breakAfter : null}
                  onChange={(m) => edit((p) => P.setAllBreaks(p, m))}
                  label="Drink break between every part"
                />
              </div>
            )}
          </div>
        )}
      </div>

      {!readOnly && (
        <>
          <aside className="hidden lg:block" aria-label="Drill library">
            <div className="sticky top-6">
              <LibraryPanel plan={plan} target={target} onTarget={setTarget} onAdd={(d) => addDrill(d)} onDragDrill={(d) => setDrag(d ? { kind: 'drill', source: d } : null)} />
            </div>
          </aside>
          <Dialog open={libraryOpen} onClose={() => setLibraryOpen(false)} title="Add from your library">
            <LibraryPanel
              plan={plan}
              target={target}
              onTarget={setTarget}
              onAdd={(d) => {
                addDrill(d);
                setLibraryOpen(false);
              }}
            />
          </Dialog>
        </>
      )}

      <Dialog open={blocker.state === 'blocked'} onClose={() => blocker.reset?.()} title="Leave without saving?">
        <p className="text-muted">You have changes to this plan that haven’t been saved.</p>
        <div className="mt-5 flex flex-wrap justify-end gap-2">
          <Button variant="secondary" onClick={() => blocker.proceed?.()}>
            Leave without saving
          </Button>
          <Button
            loading={saving.isPending}
            onClick={async () => {
              try {
                await saving.mutateAsync();
                blocker.proceed?.();
              } catch {
                blocker.reset?.();
              }
            }}
          >
            Save and leave
          </Button>
        </div>
      </Dialog>
    </div>
  );
}

function sectionOfBlock(p: Plan, blockId: string) {
  return p.sections.find((s) => s.blocks.some((b) => b.id === blockId))?.id;
}

// ---------- Top bars ----------

function Progress({ total, duration }: { total: number; duration: number }) {
  const pct = Math.min(100, (total / duration) * 100);
  const over = total > duration;
  return (
    <div className="min-w-0 flex-1">
      <p className="tnum text-[14px]">
        <span className="font-semibold">{formatMinutes(total)}</span> <span className="text-muted">planned of {formatMinutes(duration)}</span>
        {over && <span className="ml-1.5 font-medium text-danger">({formatMinutes(total - duration)} over)</span>}
        {!over && total > 0 && total < duration && <span className="ml-1.5 text-muted">({formatMinutes(duration - total)} free)</span>}
      </p>
      <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-sunken" aria-hidden>
        <div className={`h-full rounded-full ${over ? 'bg-danger' : 'bg-brand-gradient'}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function TotalBar({ total, duration }: { total: number; duration: number }) {
  return (
    <div className="mb-6">
      <Progress total={total} duration={duration} />
    </div>
  );
}

function SaveBar(props: { total: number; duration: number; dirty: boolean; saving: boolean; savedOnce: boolean; onSave: () => void; onOpenLibrary: () => void }) {
  return (
    <div className="sticky top-14 z-20 -mx-4 mb-6 border-b border-line bg-paper/95 px-4 py-3 backdrop-blur sm:-mx-8 sm:px-8 lg:top-0">
      <div className="flex items-center gap-4">
        <Progress total={props.total} duration={props.duration} />
        <button className="btn-secondary lg:hidden" onClick={props.onOpenLibrary}>
          Library
        </button>
        <div className="flex items-center gap-3">
          <span className="hidden text-[13px] text-muted sm:inline" aria-live="polite">
            {props.dirty ? 'Unsaved changes' : props.savedOnce ? 'Saved' : ''}
          </span>
          <Button onClick={props.onSave} loading={props.saving} disabled={!props.dirty} title="Save (Ctrl+S)">
            Save plan
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------- Drink breaks ----------

const BREAK_OPTIONS = Array.from({ length: MAX_BREAK + 1 }, (_, i) => i);

function BreakPicker({ value, onChange, label }: { value: number | null; onChange: (m: number) => void; label: string }) {
  return (
    <div className="inline-flex rounded-md border border-line-strong bg-surface p-0.5" role="radiogroup" aria-label={label}>
      {BREAK_OPTIONS.map((m) => (
        <button
          key={m}
          role="radio"
          aria-checked={value === m}
          onClick={() => onChange(m)}
          className={`tnum rounded px-2 py-0.5 text-[13px] ${value === m ? 'bg-action text-action-ink' : 'text-muted hover:text-ink'}`}
        >
          {m ? `${m} min` : 'None'}
        </button>
      ))}
    </div>
  );
}

/** Between two parts: time for a drink, a talk, and setting up the next drill. */
function BreakRow({ time, value, effective, readOnly, onChange }: { time: string | null; value: number; effective: number; readOnly?: boolean; onChange: (m: number) => void }) {
  if (readOnly && !effective) return null;
  return (
    <div className={`mt-5 flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-md border border-dashed px-3 py-2 text-[14px] ${effective ? 'border-sky-400/60 bg-sky-50 dark:border-sky-400/40 dark:bg-sky-950/30' : 'border-line'}`}>
      <span className="tnum w-[64px] shrink-0 font-semibold">{time ?? ''}</span>
      <GlassWater className={`size-4 shrink-0 ${effective ? 'text-sky-600 dark:text-sky-300' : 'text-faint'}`} aria-hidden />
      <span className={`min-w-0 flex-1 ${effective ? '' : 'text-muted'}`}>
        {effective ? `Drink break, ${effective} min` : value ? 'Drink break (once a later part has drills)' : 'No drink break'}
        {effective > 0 && <span className="hidden text-muted sm:inline"> · talk and set up the next drill</span>}
      </span>
      {!readOnly && <BreakPicker value={value} onChange={onChange} label="Drink break after this part" />}
    </div>
  );
}

// ---------- Sections ----------

function SectionHeader(props: {
  title: string;
  minutes: number;
  readOnly?: boolean;
  canRemove: boolean;
  first: boolean;
  last: boolean;
  onRename: (t: string) => void;
  onMove: (d: -1 | 1) => void;
  onRemove: () => void;
}) {
  return (
    <div className="flex items-center gap-2 border-b border-line-strong pb-1.5">
      {props.readOnly ? (
        <h3 className="flex-1 text-[17px] font-semibold">{props.title}</h3>
      ) : (
        <input
          aria-label="Part name"
          value={props.title}
          onChange={(e) => props.onRename(e.target.value)}
          maxLength={60}
          className="min-w-0 flex-1 rounded bg-transparent px-1 py-0.5 -ml-1 text-[17px] font-semibold hover:bg-sunken focus:bg-surface focus:outline-none focus:ring-2 focus:ring-action/25"
        />
      )}
      <span className="tnum text-[13px] whitespace-nowrap text-muted">{props.minutes ? formatMinutes(props.minutes) : ''}</span>
      {!props.readOnly && (
        <div className="flex">
          <IconButton label="Move part up" disabled={props.first} onClick={() => props.onMove(-1)}>
            <ArrowUp className="size-4" />
          </IconButton>
          <IconButton label="Move part down" disabled={props.last} onClick={() => props.onMove(1)}>
            <ArrowDown className="size-4" />
          </IconButton>
          <IconButton label={props.canRemove ? 'Remove part' : 'Move or remove its drills first'} disabled={!props.canRemove} onClick={props.onRemove}>
            <Trash2 className="size-4" />
          </IconButton>
        </div>
      )}
    </div>
  );
}

function IconButton({ label, children, ...rest }: { label: string; children: React.ReactNode; disabled?: boolean; onClick?: () => void }) {
  return (
    <button type="button" className="rounded p-1.5 text-muted hover:bg-sunken hover:text-ink disabled:pointer-events-none disabled:opacity-35" aria-label={label} title={label} {...rest}>
      {children}
    </button>
  );
}

// ---------- Time slots ----------

function DropLine({ onDrop }: { onDrop: (e: DragEvent) => void }) {
  const [over, setOver] = useState(false);
  return (
    <div
      className={`-my-1 flex h-4 items-center px-1 transition-[height] ${over ? 'h-8' : ''}`}
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        setOver(false);
        onDrop(e);
      }}
    >
      <div className={`h-0.5 w-full rounded-full ${over ? 'bg-action' : 'bg-transparent'}`} />
    </div>
  );
}

function BlockRow(props: {
  time: string;
  minutes: number;
  items: PlanItem[];
  readOnly?: boolean;
  warnings: string[];
  dragging: boolean;
  acceptsDrill: boolean;
  onDragStart: () => void;
  onDragEnd: () => void;
  onDropAlongside: (e: DragEvent) => void;
  onNudge: (d: -1 | 1) => void;
  onAddAlongside: () => void;
  onItem: (itemId: string, changes: Partial<PlanItem>) => void;
  onRemoveItem: (itemId: string) => void;
  onSplit: (itemId: string) => void;
  onSavedToLibrary: (itemId: string, drillId: string) => void;
}) {
  const [dropOver, setDropOver] = useState(false);
  const parallel = props.items.length > 1;
  const controls = !props.readOnly && (
    <>
      <span
        draggable
        onDragStart={(e) => {
          e.dataTransfer.effectAllowed = 'move';
          e.dataTransfer.setData('text/plain', 'block');
          props.onDragStart();
        }}
        onDragEnd={props.onDragEnd}
        className="hidden cursor-grab rounded p-1.5 text-muted hover:bg-sunken hover:text-ink sm:inline-flex"
        title="Drag to move"
        aria-hidden
      >
        <GripVertical className="size-4" />
      </span>
      <IconButton label="Move earlier" onClick={() => props.onNudge(-1)}>
        <ArrowUp className="size-4" />
      </IconButton>
      <IconButton label="Move later" onClick={() => props.onNudge(1)}>
        <ArrowDown className="size-4" />
      </IconButton>
      {props.items.length < 6 && (
        <IconButton label="Add a drill at the same time" onClick={props.onAddAlongside}>
          <Columns2 className="size-4" />
        </IconButton>
      )}
    </>
  );
  return (
    <div
      className={`group/block grid grid-cols-[60px_minmax(0,1fr)] gap-x-3 py-1.5 sm:grid-cols-[76px_minmax(0,1fr)_60px] sm:gap-x-2 ${props.dragging ? 'opacity-40' : ''}`}
    >
      {/* Run-sheet gutter: when this slot starts and how long it runs */}
      <div className="pt-2.5 text-right">
        <p className="tnum text-[14px] leading-tight font-semibold">{props.time}</p>
        <p className="tnum mt-0.5 text-[12px] text-muted">{props.minutes} min</p>
      </div>
      <div
        className={`min-w-0 rounded-lg ${dropOver ? 'ring-2 ring-action ring-offset-2 ring-offset-paper' : ''}`}
        onDragOver={(e) => {
          if (!props.acceptsDrill) return;
          e.preventDefault();
          setDropOver(true);
        }}
        onDragLeave={() => setDropOver(false)}
        onDrop={(e) => {
          setDropOver(false);
          props.onDropAlongside(e);
        }}
      >
        <div className={`grid grid-cols-1 gap-2 ${parallel ? (props.items.length === 2 ? 'md:grid-cols-2' : 'md:grid-cols-3') : ''}`}>
          {props.items.map((item) => (
            <ItemCard
              key={item.id}
              item={item}
              parallel={parallel}
              readOnly={props.readOnly}
              onChange={(c) => props.onItem(item.id, c)}
              onRemove={() => props.onRemoveItem(item.id)}
              onSplit={() => props.onSplit(item.id)}
              onSavedToLibrary={(drillId) => props.onSavedToLibrary(item.id, drillId)}
            />
          ))}
        </div>
        {props.warnings.map((w) => (
          <p key={w} className="mt-1.5 flex items-start gap-1.5 text-[13px] text-warn">
            <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden /> {w}
          </p>
        ))}
        {/* Phones: controls sit under the drill (no hover there) */}
        {controls && <div className="mt-0.5 -ml-1.5 flex items-center sm:hidden">{controls}</div>}
      </div>
      {/* Tablets and laptops: controls in a column on the right. With a mouse they show on hover or keyboard focus; on touch screens (iPad) they're always shown. */}
      {controls && (
        <div className="hidden content-start sm:grid sm:grid-cols-2 [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover/block:opacity-100 focus-within:opacity-100">{controls}</div>
      )}
    </div>
  );
}

// ---------- One drill ----------

function ItemCard(props: {
  item: PlanItem;
  parallel: boolean;
  readOnly?: boolean;
  onChange: (c: Partial<PlanItem>) => void;
  onRemove: () => void;
  onSplit: () => void;
  onSavedToLibrary: (drillId: string) => void;
}) {
  const { item } = props;
  const [open, setOpen] = useState(false);
  const hasDiagram = !diagramIsEmpty(item.diagram);

  return (
    <div className="rounded-lg border border-line bg-surface">
      <div className="flex items-start gap-2 px-3 py-2.5">
        <button
          className="-ml-1 mt-0.5 rounded p-0.5 text-muted hover:text-ink"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          aria-label={open ? `Hide details for ${item.title}` : `Show details for ${item.title}`}
        >
          {open ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
        </button>
        <div className="min-w-0 flex-1">
          {props.parallel && item.group && <p className="text-[12px] font-semibold text-action">{item.group}</p>}
          <button className="block w-full text-left font-medium leading-snug" onClick={() => setOpen(!open)}>
            {item.title}
          </button>
          {!open && hasDiagram && (
            <p className="mt-0.5 inline-flex items-center gap-1 text-[12px] text-action">
              <Spline className="size-3.5" aria-hidden /> Diagram
            </p>
          )}
          {!open && (item.equipment || item.coachingPoints) && (
            <p className="mt-0.5 truncate text-[13px] text-muted">{item.coachingPoints ?? item.equipment}</p>
          )}
        </div>
        {props.readOnly ? (
          <span className="tnum text-[14px] text-muted">{item.minutes} min</span>
        ) : (
          <label className="flex items-center gap-1 text-[13px] text-muted">
            <input
              type="number"
              min={1}
              max={180}
              value={item.minutes}
              onChange={(e) => props.onChange({ minutes: clampMinutes(e.target.value) })}
              className="tnum w-14 rounded-md border border-line-strong bg-surface px-1.5 py-1 text-right text-[14px] text-ink focus:border-action focus:outline-none focus:ring-2 focus:ring-action/25"
              aria-label={`Minutes for ${item.title}`}
            />
            min
          </label>
        )}
      </div>
      {open && (props.readOnly ? <ItemDetails item={item} /> : <ItemEditor {...props} onClose={() => setOpen(false)} />)}
    </div>
  );
}

function clampMinutes(v: string) {
  const n = Math.round(Number(v));
  return Number.isFinite(n) ? Math.max(1, Math.min(180, n)) : 1;
}

function ItemDetails({ item }: { item: PlanItem }) {
  const rows: [string, string | null][] = [
    ['How it runs', item.description],
    ['Coaching points', item.coachingPoints],
    ['Equipment', item.equipment],
    ['Coach notes', item.notes],
  ];
  const shown = rows.filter(([, v]) => v);
  return (
    <dl className="space-y-2 border-t border-line px-3 py-2.5 pl-9 text-[14px]">
      {item.diagram && !diagramIsEmpty(item.diagram) && (
        <div className="pb-1">
          <DiagramView diagram={item.diagram} />
        </div>
      )}
      {shown.length || item.diagram ? (
        shown.map(([k, v]) => (
          <div key={k}>
            <dt className="text-[12px] font-semibold text-muted">{k}</dt>
            <dd className="whitespace-pre-line">{v}</dd>
          </div>
        ))
      ) : (
        <p className="text-muted">No details for this drill.</p>
      )}
    </dl>
  );
}

function ItemEditor(props: {
  item: PlanItem;
  parallel: boolean;
  onChange: (c: Partial<PlanItem>) => void;
  onRemove: () => void;
  onSplit: () => void;
  onSavedToLibrary: (drillId: string) => void;
  onClose: () => void;
}) {
  const { item, onChange } = props;
  const qc = useQueryClient();
  const [drawing, setDrawing] = useState(false);
  const toLibrary = useMutation({
    mutationFn: () =>
      api.post<Drill>('/drills', {
        title: item.title,
        minutes: item.minutes,
        description: item.description,
        coachingPoints: item.coachingPoints,
        equipment: item.equipment,
        minPlayers: item.minPlayers,
        courts: item.courts,
        tags: item.tags,
        diagram: item.diagram,
      }),
    onSuccess: (d) => {
      props.onSavedToLibrary(d.id);
      void qc.invalidateQueries({ queryKey: ['drills'] });
    },
  });

  const text = (key: 'title' | 'group' | 'description' | 'coachingPoints' | 'equipment' | 'notes') => ({
    value: item[key] ?? '',
    onChange: (e: { target: { value: string } }) => onChange({ [key]: key === 'title' ? e.target.value : e.target.value || null }),
  });

  return (
    <div className="space-y-3 border-t border-line px-3 py-3">
      <div className={`grid gap-3 ${props.parallel ? 'sm:grid-cols-[1fr_140px]' : ''}`}>
        <MiniField label="Drill name">
          <input className="field-input" maxLength={120} {...text('title')} />
        </MiniField>
        {props.parallel && (
          <MiniField label="Group">
            <input className="field-input" maxLength={40} placeholder="e.g. Guards" {...text('group')} />
          </MiniField>
        )}
      </div>
      <MiniField label="How it runs">
        <textarea className="field-input min-h-[72px]" maxLength={4000} {...text('description')} />
      </MiniField>
      <MiniField label="Coaching points">
        <textarea className="field-input min-h-[60px]" maxLength={4000} {...text('coachingPoints')} />
      </MiniField>
      <div className="grid gap-3 sm:grid-cols-2">
        <MiniField label="Equipment">
          <input className="field-input" maxLength={500} placeholder="e.g. 6 cones, 4 balls" {...text('equipment')} />
        </MiniField>
        <div className="grid grid-cols-2 gap-3">
          <MiniField label="Min. players">
            <input
              className="field-input tnum"
              type="number"
              min={1}
              max={60}
              value={item.minPlayers ?? ''}
              onChange={(e) => onChange({ minPlayers: e.target.value ? Math.max(1, Math.min(60, Math.round(Number(e.target.value)))) : null })}
            />
          </MiniField>
          <MiniField label="Courts">
            <input
              className="field-input tnum"
              type="number"
              min={1}
              max={6}
              value={item.courts ?? ''}
              onChange={(e) => onChange({ courts: e.target.value ? Math.max(1, Math.min(6, Math.round(Number(e.target.value)))) : null })}
            />
          </MiniField>
        </div>
      </div>
      <div className="space-y-1">
        <span className="block text-[13px] font-medium">Court diagram</span>
        {item.diagram && !diagramIsEmpty(item.diagram) ? (
          <div className="space-y-2">
            <DiagramView diagram={item.diagram} />
            <Button type="button" variant="secondary" onClick={() => setDrawing(true)}>
              <Spline className="size-4" aria-hidden /> Edit diagram
            </Button>
          </div>
        ) : (
          <Button type="button" variant="secondary" onClick={() => setDrawing(true)}>
            <Spline className="size-4" aria-hidden /> Draw a diagram
          </Button>
        )}
        <DiagramEditorDialog
          open={drawing}
          value={item.diagram}
          title={`Diagram: ${item.title}`}
          onClose={() => setDrawing(false)}
          onSave={(diagram) => {
            onChange({ diagram });
            setDrawing(false);
          }}
        />
      </div>
      <div className="space-y-1">
        <span className="block text-[13px] font-medium">Skills</span>
        <TagPicker value={item.tags ?? []} onChange={(tags) => onChange({ tags })} compact />
        <span className="block text-[12px] text-muted">Used in the season view to show what you’ve worked on.</span>
      </div>
      <MiniField label="Notes for this session (coaches only)">
        <textarea className="field-input min-h-[52px]" maxLength={2000} {...text('notes')} />
      </MiniField>
      <p className="text-[12px] text-muted">Changes here only affect this plan, not your drill library.</p>
      {toLibrary.isError && <Alert>{errorMessage(toLibrary.error)}</Alert>}
      <div className="flex flex-wrap items-center gap-2">
        {!item.sourceDrillId && (
          <Button variant="secondary" loading={toLibrary.isPending} onClick={() => toLibrary.mutate()}>
            <BookmarkPlus className="size-4" aria-hidden /> Save to my library
          </Button>
        )}
        {item.sourceDrillId && toLibrary.isSuccess && <span className="text-[13px] text-ok">Saved to your library.</span>}
        {props.parallel && (
          <Button variant="secondary" onClick={props.onSplit}>
            <Rows2 className="size-4" aria-hidden /> Run on its own
          </Button>
        )}
        <Button variant="danger" onClick={props.onRemove}>
          <Trash2 className="size-4" aria-hidden /> Remove
        </Button>
        <button className="ml-auto text-[14px] text-muted hover:text-ink" onClick={props.onClose}>
          Done
        </button>
      </div>
    </div>
  );
}

function MiniField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1">
      <span className="block text-[13px] font-medium">{label}</span>
      {children}
    </label>
  );
}
